import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { CuboidStyleHandlers, StyledCuboid } from '@/modules/cuboids/types';
import { AnalysisCuboid, FindViewpointsToolState, PartialExcept } from '@/types';
import { DEFAULT_CUBOID_COLOR, SELECTED_CUBOID_COLOR } from '../constants';
import { CuboidStyle, CuboidVariants } from '@fabric-web/3d-viewer';
import * as findViewpointsState from '../../findViewpointsTool/state';

const makeCuboidStyle = (
  partialStyles: PartialExcept<CuboidStyle, 'fillColor' | 'lineColor'>
): CuboidStyle => ({
  variant: partialStyles?.variant || CuboidVariants.LinesAndTransparentMesh,
  lineColor: partialStyles?.lineColor,
  lineWidth: partialStyles?.lineWidth || 1,
  lineOpacity: partialStyles?.lineOpacity || 0.8,
  fillColor: partialStyles?.fillColor,
  fillOpacity: partialStyles?.fillOpacity || 0.2,
});

export const useCuboidStyleHandlers = (): CuboidStyleHandlers<AnalysisCuboid> => {
  const findViewpointsToolState = useAtomValue(findViewpointsState.findViewpointsToolState);
  const isFindViewpointsToolActive = findViewpointsToolState !== FindViewpointsToolState.INACTIVE;
  const makeCuboidStyles = useCallback(
    (cuboid: AnalysisCuboid, options?: Partial<CuboidStyle>) => {
      const color = DEFAULT_CUBOID_COLOR;
      const variant = isFindViewpointsToolActive
        ? CuboidVariants.LinesOnly
        : CuboidVariants.LinesAndTransparentMesh;
      const style = makeCuboidStyle({ variant, lineColor: color, fillColor: color, ...options });
      return {
        style,
        hoveredStyle: style,
        enableClippingByVisibilityBox: false,
      };
    },
    [isFindViewpointsToolActive]
  );

  const makeCurrentCuboidStyle = useCallback(
    (cuboid: StyledCuboid<AnalysisCuboid>, options?: Partial<CuboidStyle>) => {
      const color = SELECTED_CUBOID_COLOR;
      const variant = isFindViewpointsToolActive
        ? CuboidVariants.LinesOnly
        : CuboidVariants.LinesAndTransparentMesh;
      return makeCuboidStyle({ variant, lineColor: color, fillColor: color, ...options });
    },
    [isFindViewpointsToolActive]
  );

  const styleHandlers = useMemo(
    () => ({
      makeCuboidStyles,
      makeCurrentCuboidStyle,
    }),
    [makeCuboidStyles, makeCurrentCuboidStyle]
  );

  return styleHandlers;
};
